import common from '@portal/common/locale/en/common';
import {
    TYPE_ARROW_CHART,
    TYPE_BAR_CHART,
    TYPE_LINE_CHART,
    TYPE_MAP_CHART,
    TYPE_TREEMAP_CHART,
} from '@portal/common/models/chart-type';
import {
    APPARENT_ID_KEY,
    CAUSE_ID_KEY,
    COVARIATE_ID_KEY,
    CREATED,
    CRO_ID_KEY,
    INDICATOR_ID_KEY,
    INFECTION_TYPE_ID_KEY,
    INFECTIOUS_SYNDROME_ID_KEY,
    OPTION_COMMENT,
    OPTION_ID_KEY,
    ORGANIZATION_KEY,
    OUTCOME_ID_KEY,
    PATHOGEN_ID_KEY,
    PAYER_ID_KEY,
    QUESTION_ID_KEY,
    REI_ID_KEY,
    RESPONDENT_KEY,
    RISK_EXPOSURE_ID_KEY,
    SDOH_ID_KEY,
    SEQUELA_ID_KEY,
    SEVERITY_ID_KEY,
    SURVEY_ID_KEY,
    SURVEY_STATUS,
    SYMPTOMATIC_ID_KEY,
    TOC_ID_KEY,
    VACCINATION_STATUS_ID_KEY,
    VEHSS_CONDITION_ID_KEY,
} from '@portal/common/models/data-key';
import {
    AntimicrobialResistanceRecords,
    CauseAssociatedCoprevalenceRecords,
    CauseCoprevalenceRecords,
    CauseOutcome,
    CovariateRecords,
    CroRecords,
    EDPOnetimeIndicators,
    EDPTimelineIndicators,
    EtiologyRecords,
    ExpenditureRecords,
    HealthThreatsMetricsRecords,
    ImpairmentRecords,
    InjuryRecords,
    MultipleMyelomaOutputShell,
    MultipleMyelomaSurvivalResults,
    ProjectedComorbidityCausesOutcomeRecords,
    RaceEthnicityEyeConditionsRecords,
    RiskAttribution,
    RiskExposureRecords,
    RiskPrevalence,
    SdohRecords,
    SequelaOutcome,
    SevRecords,
    TakedaDengueRecords,
} from '@portal/common/models/data-type';

export default {
    // portal common
    ...common,

    response_1099: 'DTR/Unknown (High Risk)',
    response_2099: 'DTR/Unknown (Current Smoker)',
    response_3099: 'DTR/Unknown (Chronic condition)',
    response_4099: 'DTR/Unknown (Senior citizen)',
    response_5099: 'DTR/Unknown (Flu vaccine)',
    response_6099: 'DTR/Unknown (Covid vaccine)',
    response_7099: 'DTR/Unknown (Safe behaviors)',
    response_8099: 'DTR/Unknown (Stayed home)',
    response_9099: 'DTR/Unknown (Face mask)',
    response_10099: 'DTR/Unknown (Wash hands)',
    response_11099: 'DTR/Unknown (Clean space)',
    response_12099: 'DTR/Unknown (Daily temperature categories)',
    response_13099: 'DTR/Unknown (Symptoms reported daily)',
    response_14099: 'DTR/Unknown (Daily blood oxygen saturation categories)',

    // Data types
    data_type_dropdown_label: 'Types',

    // Refinements
    refinement_day: 'Dates',

    // Domain Terms
    global_email: 'Email',
    organization_role_member: 'Member',
    organization_role_manager: 'Manager',

    // Common Messages
    delete_item_question: 'Are you sure you want to delete this item?',

    // Common Errors
    error_server: 'Server error',
    error_field_is_required: 'This field is required',
    error_short_field_is_required: 'Required',
    error_invalid_email: 'Invalid email format',
    ie_compatibility_notice:
        'We are working to resolve some technical issues with how the Client Portal functions in Internet Explorer. If you are using IE, please try opening this link in another browser.',
    error_user_has_no_organization:
        "We're sorry but you cannot access this site. Your user is not a member of organization.",
    error_disabled_organization:
        "We're sorry but you cannot access this site. Your organization is disabled.",
    error_only_csv: 'File: Only .csv file types are supported.',
    error_no_valid_emails:
        'File: No email addresses were found. Please upload a CSV file containing valid email addresses.',
    error_not_valid_csv_file: 'File: Not valid csv file.',
    error_short_password: 'Password should be at least 8 characters long',
    error_required_field: "This field can't be empty",
    error_passwords_must_match: 'Passwords have to match',

    // Navigation
    nav_group_main: 'Main',
    nav_activity: 'Activity',
    nav_insights: 'Insights',
    nav_group_content: 'Content',
    nav_categories: 'Categories',
    nav_links: 'Links',
    nav_files: 'Files',
    nav_data_collections: 'Data Collections',
    nav_data_collections_create: 'Create Collection',
    nav_data_collections_edit: 'Edit Collection',
    nav_data_collection_settings: 'Collection Settings',
    nav_data_collection_default_filters_values: 'Default Filters Values',
    nav_content_management: 'Content Management',
    nav_surveys: 'Surveys',

    nav_group_clients: 'Clients',
    nav_organizations: 'Organizations',

    nav_group_ihme: 'IHME',
    nav_staff: 'Staff Directory',
    nav_settings: 'Settings',

    nav_profile: 'Profile',
    nav_change_password: 'Change Password',
    nav_sign_in: 'Log In',
    nav_sign_out: 'Log Out',

    nav_content: 'Content',

    // Sign In scene
    sign_in_scene_sign_in_button: 'Sign In',
    sign_in_scene_forgot_password_link: 'Forgot password?',

    // ForgotPasswordScene
    forgot_password_reset: 'Password reset',
    forgot_password_success_reset:
        'You have been sent a new password. Please log in with your new password.',
    forgot_password_title: 'Reset Password',
    forgot_password_reset_button: 'Reset',
    forgot_password_description:
        'To reset your password, please confirm your email address below.\n\nYou will receive an email with a new password, after which you can go back and log in again.',

    // OrganizationsScene
    organizations_name_table_column: 'Organization',
    organizations_members: 'Members',
    organizations_links: 'Links',
    organizations_files: 'Files',
    organizations_custom_welcome: 'Custom Welcome',
    organizations_actions: 'Actions',
    organizations_access: 'Access',
    organizations_add_button: 'Add Organization',

    // EditOrganizationModal
    // OrganizationProfile
    organization_id: 'ID',
    organization_add_title: 'Add Organization',
    organization_edit_title: 'Edit Organization',
    organization_name: 'Name',
    organization_website_url: 'Website URL',
    organization_custom_url: 'Custom Portal URL',
    organization_custom_url_none: 'None',
    organization_avatar_url: 'Avatar',
    organization_address: 'Address',
    organization_phone_number: 'Phone number',
    organization_email_domains: 'Email domains',
    organization_edit_organization: 'Edit Organization',
    organization_disable_organization: 'Disable Organization Access',
    organization_enable_organization: 'Enable Organization Access',
    organization_toggle_organization_access_disabled_tooltip:
        'Access cannot be given if license is expired',
    organization_email_domains_hint:
        'Enter a comma-separated list of email domains that members of this Organization should belong to in order to be allowed to be invited. Leave empty for no restriction.',
    organization_self_sign_up_token: 'Self registration URL',
    organization_self_sign_up_mode: 'Self registration mode',
    organization_self_sign_up_mode_none: 'Not Allowed',
    organization_self_sign_up_mode_email: 'Email Based', // @deprecated
    organization_self_sign_up_mode_domain: 'Email Based',
    organization_self_sign_up_mode_link: 'URL Based', // @deprecated
    organization_self_sign_up_mode_url: 'URL Based',
    organization_copy_link: 'Copy to clipboard',
    organization_regenerate_link: 'Regenerate URL',

    // OrganizationScene
    organization_profile: 'Profile',
    organization_members: 'Members',
    organization_links: 'Links',
    organization_files: 'Files',
    organization_collections: 'Collections',
    organization_public_lists: 'Public Lists',
    organization_welcome_message: 'Welcome Message',
    organization_warning_message: 'License Expiration',
    organization_has_programmatic_access: 'Programmatic Access',
    organization_has_programmatic_access_false: 'Not Allowed',
    organization_has_programmatic_access_true: 'Allowed',
    organization_ai_chat_is_enabled: 'AI Search',
    organization_licensed_until: 'Licensed until',
    organization_license_expired: 'License expired on %date%',
    organization_settings: 'Settings',
    organization_settings_search: 'Search',
    organization_pharma_products_search_is_enabled: 'Search by drug/brand names',
    organization_pharma_products_search_is_company_restricted:
        'Restrict search to company products only',
    organization_health_threat_metrics_alert_is_enabled: 'GHT data update alert',
    organization_forecast_data_calculation_is_allowed: 'Pre-published estimates',
    organization_forecast_data_calculation_years: 'Years',
    organization_forecast_data_calculation_years_hint: 'Allowed values: 1-10',

    // OrganizationMembers
    organization_member_name: 'Name',
    organization_member_first_name: 'First Name',
    organization_member_last_name: 'Last Name',
    organization_member_email: 'Email',
    organization_member_password: 'Password',
    organization_member_avatar_url: 'Avatar',
    organization_member_job_title: 'Job Title',
    organization_member_title: 'Job Title',
    organization_member_role: 'Role',
    organization_member_organization_role: 'Role',
    organization_member_role_member: 'Member',
    organization_member_role_manager: 'Manager',
    organization_member_last_access: 'Last Access',
    organization_member_status: 'Status',
    organization_member_is_stealth: 'Track User Activity In Insights',
    organization_member_actions: 'Actions',
    organization_members_invite: 'Invite Member',
    organization_members_bulk_invite: 'Bulk Invite (CSV)',
    organization_member_add_title: 'Add member',
    organization_member_edit_title: 'Edit member',
    organization_member_delete_title: 'Remove Member',
    organization_member_delete_question: 'Are you sure you want to remove %name%?',
    organization_member_select_role: 'Select role',
    organization_member_has_programmatic_access: 'Programmatic access',

    modal_close: 'Close',
    modal_done: 'Done',

    // OrganizationMemberEditModal
    account_first_name: 'First Name',
    account_last_name: 'Last Name',
    account_email: 'Email',
    account_password: 'Password',
    account_title: 'Title',
    account_generate_password_button: 'Generate password',

    // OrganizationLinks
    organization_link_manage_links: 'Manage Links',
    organization_link_name: 'Title',
    organization_link_url: 'URL',
    organization_link_category: 'Category',
    organization_link_date: 'Date', // @deprecated
    organization_link_created: 'Created',
    organization_link_actions: 'Actions',

    // OrganizationFiles
    organization_file_manage_files: 'Manage Files',
    organization_file_name: 'Title',
    organization_file_category: 'Category',
    organization_file_file_size: 'File Size', // @deprecated
    organization_file_size: 'Size',
    organization_file_date: 'Date', // @deprecated
    organization_file_created: 'Created',
    organization_file_actions: 'Actions',

    // OrganizationCollections
    organization_collection_manage_collections: 'Manage Collections',
    organization_collection_name: 'Title',
    organization_collection_description: 'Description',
    organization_collection_date: 'Date', // @deprecated
    organization_collection_created: 'Created',
    organization_collection_actions: 'Actions',

    organization_collection_name_data_collection: 'Name Collection',
    select_data_project: 'Select Data Project',
    data_project_dropdown_label: 'Project',
    data_project_search_placeholder: 'Search project...',
    data_collection_select_data_types: 'Select Data Types',
    data_collection_data_type_placeholder: 'Select type...',
    data_collection_select_granularity: 'Select Granularity',
    select_default_granularity_filters: 'Select Default Granularity Filters',

    // OrganizationPublicLists
    organization_public_lists_name: 'Name',
    organization_public_lists_description: 'Description',
    organization_public_lists_creator: 'Creator',
    organization_public_lists_is_default: 'Is Default',

    // OrganizationSetting
    organization_settings_export_options: 'Export Options',
    organization_settings_single_age_estimates_report: 'Custom Age Estimates',
    organization_settings_save_changes: 'Save Changes',
    organization_settings_excel_report: 'Excel Report',
    organization_settings_CSV_report: 'CSV Report',
    organization_settings_PNG_chart: 'PNG Chart',
    organization_settings_GIF_chart: 'GIF Chart',
    organization_settings_PDF_report: 'PDF Report',
    organization_settings_enabled_charts: 'Chart Types',
    [`organization_settings_${TYPE_LINE_CHART}_chart`]: 'Timeline',
    [`organization_settings_${TYPE_MAP_CHART}_chart`]: 'Map',
    [`organization_settings_${TYPE_BAR_CHART}_chart`]: 'Bar',
    [`organization_settings_${TYPE_TREEMAP_CHART}_chart`]: 'Treemap',
    [`organization_settings_${TYPE_ARROW_CHART}_chart`]: 'Arrow',

    // ContentScene
    content_links: 'Links',
    content_files: 'Files',
    content_categories: 'Categories',

    // |- ContentLinks
    content_link_add_title: 'Add Link',
    content_link_edit_title: 'Edit Link',
    content_link_delete_title: 'Delete Link',
    content_link_name: 'Title',
    content_link_name_placeholder: 'Title',
    content_link_url: 'URL',
    content_link_resource_category_id: 'Category',
    content_link_resource_category_id_placeholder: 'Category',
    content_link_keywords: 'Keywords',
    content_link_keywords_placeholder: 'Type keyword and press enter, tab or space...',
    content_link_date: 'Date',
    content_link_shared_with: 'Shared',
    content_link_actions: 'Actions',
    content_link_share_title: 'Share Link',
    content_link_created_by: 'Created By',

    // |- ContentFiles
    content_file_add_title: 'Add File',
    content_file_edit_title: 'Edit File',
    content_file_delete_title: 'Delete File',
    content_file_url: 'File',
    content_file_name: 'Title',
    content_file_name_placeholder: 'Title',
    content_file_resource_category_id: 'Category',
    content_file_resource_category_id_placeholder: 'Category',
    content_file_keywords: 'Keywords',
    content_file_keywords_placeholder: 'Type keyword and press enter, tab or space...',
    content_file_file_size: 'Size',
    content_file_date: 'Date',
    content_file_shared_with: 'Shared',
    content_file_actions: 'Actions',
    content_file_share_title: 'Share File',
    content_file_created_by: 'Created By',

    // |- ContentCategories
    content_category_add_title: 'Add Category',
    content_category_edit_title: 'Edit Category',
    content_category_delete_title: 'Delete Category',
    content_category_name: 'Title',
    content_category_description: 'Description',
    content_category_date: 'Date',
    content_category_items_tagged: 'Items Tagged',
    content_category_actions: 'Actions',
    content_category_created_by: 'Created By',

    // StaffScene
    staff_invite_staff: 'Invite Staff',
    staff_invite: 'Invite Staff', // @deprecated
    staff_name: 'Name',
    staff_title: 'Title',
    staff_email: 'Email',
    staff_email_placeholder: 'Email of staff to be invited',
    staff_is_public: 'In Public Directory',

    // Profile Scene
    profile_scene_title: 'Profile & Settings',
    profile_general_tab: 'General',
    profile_change_password_tab: 'Change Password',
    profile_sign_out_tab: 'Log out',
    profile_save_changes: 'Save Changes',
    profile_first_name: 'First Name',
    profile_last_name: 'Last Name',
    profile_email: 'Email Address',
    profile_title: 'Job Title',
    profile_is_public: 'Show my profile in Public Directory',
    profile_success_save: 'Saved successfully!',
    profile_avatar_url: 'Avatar',

    // EditPictureModal
    edit_picture_add_title: 'Add Picture',
    edit_picture_edit_title: 'Update Picture',
    edit_picture_add_button: 'Add Picture',
    edit_picture_update_button: 'Update Picture',
    edit_picture_url: 'File',

    // ChangePasswordScene
    change_password_button: 'Change Password',
    password: 'Password',
    new_password: 'New Password',
    new_password_confirmation: 'Confirm New Password',

    // ConfirmationModal
    cancel_confirmation_question: 'Are you sure you want to cancel?',
    cancel_confirmation_title: 'Cancel download',
    cancel_confirmation_button: 'Confirm',

    // FileUploadInput
    file_upload_input_choose_file_button_label: 'Choose File',
    file_upload_input_upload_button_label: 'Upload a new file',
    file_upload_input_cancel_button_label: 'Cancel upload',
    file_upload_input_overwrite_warning: 'WARNING: This action will overwrite the old file',

    footer_note: ` `,

    // Activity Scene
    activity_load_more: 'Load More',
    recipient_from: 'from the',
    recipient_to: 'to the',
    recipient_in: 'in the',
    recipient_with: 'with the',
    action_added: 'added',
    action_updated: 'updated',
    action_removed: 'removed',
    action_shared: 'shared',
    action_unshared: 'unshared',
    action_invited: 'invited',
    action_uploaded: 'uploaded',
    action_enabled: 'enabled',
    action_disabled: 'disabled',
    entity_profile: 'profile of',
    entity_staff: 'staff member',
    entity_own_profile: 'their profile',
    entity_organization: 'organization',
    entity_organization_member: 'member',
    entity_category: 'category',
    entity_link: 'link',
    entity_file: 'file',
    entity_announcement: 'announcement',
    entity_sidebar_link: 'sidebar link',
    entity_welcome_message: 'welcome message',
    entity_data_collection: 'data collection',
    entity_smart_option: 'smart option',
    entity_survey: 'survey',
    entity_methodology: 'methodology',
    activity_scene_member: 'Member',
    activity_scene_activity: 'Activity',
    activity_scene_time: 'Time',
    logged_in: 'logged in',
    as: 'as',

    // Form fields
    form_field_first_name: 'First Name',
    form_field_last_name: 'Last Name',
    form_field_email: 'Email Address',
    form_field_title: 'Title',
    form_field_password: 'Password',
    form_field_new_password: 'New Password',
    form_field_new_password_confirmation: 'Confirm New Password',
    form_field_organization_role: 'Role',

    lists_list_is_empty: "This list doesn't have any items",

    confirm_label: 'Confirm',
    share_label: 'Share With:',

    // DataCollections Scene
    data_collections_empty_text:
        'You haven’t created any Data Collections to share with your organizations. Get started now by adding a new collection.',
    data_collections_add_button_label: 'Add Collection',
    data_collections_save_button_label: 'Save Collection',
    data_collections_create_button_label: 'Create Collection',
    data_collections_save_success: 'Collection Saved Successfully',
    data_collection_name: 'Name',
    data_collection_description: 'Description',
    data_collection_date: 'Date',
    data_collection_coverage: 'Coverage',
    data_collection_shared_with: 'Shared',
    data_collection_created_by: 'Created By',
    data_collection_actions: 'Actions',
    data_collection_delete_title: 'Delete Data Collection',
    data_collection_share_title: 'Share Data Collection',
    data_collection_delete_shared_collection_title:
        'Collection is Shared with One or More Organization',
    data_collection_delete_shared_collection_message:
        'Collections shared with organizations cannot be deleted. Please unshare the collection and then try again.',
    data_collection_tooltip_copy: 'Copy Data Collection',
    data_collection_title_with_copy: 'Copy of',
    data_collection_scroll_to_default_filters_button: 'Scroll To Default Filters Section',
    data_collection_should_select_all_related_sequela_toggle_title: 'Select all related sequela',

    // Surveys Scene
    surveys_empty_text:
        'You haven’t created any Surveys to share with your organizations. Get started now by adding a new survey.',
    surveys_create_button_label: 'Add Survey',
    surveys_save_success: 'Survey Saved Successfully',
    survey_name: 'Name',
    survey_description: 'Description',
    survey_date: 'Date',
    survey_created_by: 'Created By',
    survey_shared_with: 'Shared',
    survey_actions: 'Actions',
    survey_is_published: 'Is Published',
    survey_responses_dismissed: '# Responses/ Dismissed',
    survey_delete_title: 'Delete Survey',
    survey_share_title: 'Share Survey',
    survey_delete_shared_survey_title: 'Survey is Shared with One or More Organization',
    survey_delete_shared_survey_message:
        'Surveys shared with organizations cannot be deleted. Please unshare the survey and then try again.',
    surveys_create_title: 'Create Survey',
    surveys_edit_title: 'Edit Survey',
    survey_responses_csv_filename: 'Survey Responses - %title%',
    save_survey_button: 'Save Survey',
    create_survey_button: 'Create Survey',

    // InsightsScene
    insights_visitors_title: 'Visits',
    insights_pages_title: 'Pages',
    insights_files_title: 'File',
    insights_data_title: 'Data',
    insights_exports_title: 'Exports',
    insights_pdf_exports_title: 'PDF Exports',
    insights_user_feedbacks_title: 'User Feedback',
    insights_ai_chats_title: 'AI Chats',
    insights_ai_chats_id: 'Chat ID',
    insights_ai_chats_prompt: 'First prompt',
    insights_ai_chats_messages: 'Messages',
    insights_ai_chats_created: 'Created',
    insights_ai_chats_search_placeholder: 'Search by member name or email',
    insights_ai_chat_detail_mode_selector_label: 'AI chat detail view mode',
    insights_ai_chat_detail_chat_label: 'AI conversation history',
    insights_ai_feedback_title: 'AI Feedback',
    insights_ai_feedback_member: 'Member',
    insights_ai_feedback_email: 'Email',
    insights_ai_feedback_prompt: 'First prompt',
    insights_ai_feedback_response: 'AI response',
    insights_ai_feedback_comment: 'Comment',
    insights_ai_feedback_reaction: 'Reaction',
    insights_ai_feedback_reaction_like: 'Positive',
    insights_ai_feedback_reaction_dislike: 'Negative',
    insights_ai_feedback_reaction_all: 'All Feedback',
    insights_ai_feedback_raw_data: 'Raw Data',
    insights_ai_feedback_raw_data_copy: 'Copy',
    insights_ai_feedback_raw_data_copied: 'Copied',
    insights_ai_feedback_detail_mode_selector_label: 'Feedback detail view mode',
    insights_ai_feedback_detail_chat_label: 'AI feedback conversation history',
    insights_all_organizations_label: 'All Organizations',
    insights_company_usage_title: 'Company Usage',
    insights_search_title: 'Search',

    insights_filter_visitors: 'Measure',
    insights_filter_from: 'From',
    insights_filter_to: 'To',
    filter_organizations_label: 'Organizations',
    filter_organization_label: 'Organization',
    insights_filter_data_project: 'Data Project',
    insights_filter_data_project_placeholder: 'Select data project...',
    insights_filter_data_projects_placeholder: 'Select data projects...',
    insights_filter_chart_type: 'Chart Type',
    insights_filter_time_unit: 'Time Unit',
    insights_filter_time_unit_button_group: 'View by',
    insights_filter_group_by: 'Group By',
    insights_filter_group_by_placeholder: 'Select group by...',
    insights_filter_select_columns: 'Select Columns',
    insights_filter_delivery_method: 'Delivery Method',
    insights_delivery_method_download: 'Downloads',
    insights_delivery_method_email: 'Emailed',
    insights_filter_visualizations: 'Visualizations',
    insights_filter_visualizations_placeholder: 'Select visualizations...',

    insights_visualization_top_locations: 'Top Locations',
    insights_visualization_top_locations_non_licensed_location_tooltip: 'Non-licensed',
    insights_visualization_total_visits: 'Total Visits',
    insights_visualization_total_visits_title: 'Total Visits by %timeUnit%',
    insights_visualization_total_queries: 'Total Queries',
    insights_visualization_total_queries_title: 'Total Queries by %timeUnit%',
    insights_visualization_engagement_trends: 'Engagement Trends',
    insights_visualization_engagement_trends_title: 'Engagement Trends by %timeUnit%',
    insights_visualization_license_information: 'License Information ',
    insights_visualization_queries_by_data_type: 'Queries By Data Type',
    insights_visualization_query_volume: 'Query Volume',
    insights_visualization_query_volume_y_axis_name: '# of queries',
    insights_visualization_query_volume_location_id: 'Query Volume, Locations',

    [`insights_visualization_query_volume_${AntimicrobialResistanceRecords}_${PATHOGEN_ID_KEY}`]:
        'Query Volume, AMR Pathogens',
    [`insights_visualization_query_volume_${CauseAssociatedCoprevalenceRecords}_${CAUSE_ID_KEY}`]:
        'Query Volume, Causes (Associated Coprevalence)',
    [`insights_visualization_query_volume_${CauseCoprevalenceRecords}_${CAUSE_ID_KEY}`]:
        'Query Volume, Causes (Coprevalence)',
    [`insights_visualization_query_volume_${CauseOutcome}_${CAUSE_ID_KEY}`]:
        'Query Volume, Causes (Cause Outcome)',
    [`insights_visualization_query_volume_${CovariateRecords}_${COVARIATE_ID_KEY}`]:
        'Query Volume, Covariates',
    [`insights_visualization_query_volume_${EDPOnetimeIndicators}_${INDICATOR_ID_KEY}`]:
        'Query Volume, Early Detection Onetime Indicators',
    [`insights_visualization_query_volume_${EDPTimelineIndicators}_${INDICATOR_ID_KEY}`]:
        'Query Volume, Early Detection Timeline Indicators',
    [`insights_visualization_query_volume_${EtiologyRecords}_${REI_ID_KEY}`]:
        'Query Volume, Etiologies',
    [`insights_visualization_query_volume_${ExpenditureRecords}_${CAUSE_ID_KEY}`]:
        'Query Volume, Causes (Expenditure)',
    [`insights_visualization_query_volume_${HealthThreatsMetricsRecords}_${CAUSE_ID_KEY}`]:
        'Query Volume, Causes (Health Threat Metrics)',
    [`insights_visualization_query_volume_${ImpairmentRecords}_${REI_ID_KEY}`]:
        'Query Volume, Impairments',
    [`insights_visualization_query_volume_${InjuryRecords}_${REI_ID_KEY}`]:
        'Query Volume, Injuries',
    [`insights_visualization_query_volume_${MultipleMyelomaOutputShell}_${CAUSE_ID_KEY}`]:
        'Query Volume, Causes (Multiple Myeloma Output Shell)',
    [`insights_visualization_query_volume_${MultipleMyelomaSurvivalResults}_${CAUSE_ID_KEY}`]:
        'Query Volume, Causes (Multiple Myeloma Survival Results)',
    [`insights_visualization_query_volume_${ProjectedComorbidityCausesOutcomeRecords}_${CAUSE_ID_KEY}`]:
        'Query Volume, Causes (Projected Comorbidity)',
    [`insights_visualization_query_volume_${RaceEthnicityEyeConditionsRecords}_${VEHSS_CONDITION_ID_KEY}`]:
        'Query Volume, Eye Conditions',
    [`insights_visualization_query_volume_${RiskAttribution}_${REI_ID_KEY}`]:
        'Query Volume, Risk Attributions',
    [`insights_visualization_query_volume_${RiskExposureRecords}_${REI_ID_KEY}`]:
        'Query Volume, Risk Exposures',
    [`insights_visualization_query_volume_${RiskPrevalence}_${REI_ID_KEY}`]:
        'Query Volume, Risk Prevalences',
    [`insights_visualization_query_volume_${SdohRecords}_${SDOH_ID_KEY}`]: 'Query Volume, SDoHs',
    [`insights_visualization_query_volume_${SequelaOutcome}_${SEQUELA_ID_KEY}`]:
        'Query Volume, Sequelas',
    [`insights_visualization_query_volume_${SevRecords}_${REI_ID_KEY}`]: 'Query Volume, SEVs',
    [`insights_visualization_query_volume_${TakedaDengueRecords}_${OUTCOME_ID_KEY}`]:
        'Query Volume, Dengue Case Outcomes',
    [`insights_visualization_query_volume_${CroRecords}_${CRO_ID_KEY}`]:
        'Query Volume, Granular Resolutions',
    [`insights_visualization_query_volume_${CroRecords}_${CAUSE_ID_KEY}`]:
        'Query Volume, Granular Resolution Causes',

    insights_visualization_line_chart_y_axis_name: 'Number',
    insights_visualization_line_chart_x_axis_name: '%timeUnit%s',

    insights_visit_chart_title: '%measure% by %unit%s, %dateRange%',
    insights_visit_chart_subtitle: '%groupBy%', //, Organizations: %organizations%',

    insights_pageView_chart_title: 'Pages Visited by %unit%s, %dateRange%',
    insights_pageView_chart_subtitle: '%groupBy%', //'Organizations: %organizations%',

    insights_fileDownload_chart_title: 'Files Downloaded by %unit%s, %dateRange%',
    insights_fileDownload_chart_subtitle: '%groupBy%', //'Organizations: %organizations%',

    insights_dataQuery_chart_title: 'Data Queries by %unit%s, %dateRange%',
    insights_dataQuery_chart_subtitle: '%groupBy%', //'Organizations: %organizations%',

    insights_dataExport_chart_title: 'Exports by %unit%s, %dateRange%',
    insights_dataExport_chart_subtitle: '%groupBy%', //'Organizations: %organizations%',

    insights_pdfExport_chart_title: 'PDF Exports by %unit%s, %dateRange%',
    insights_pdfExport_chart_subtitle: '%groupBy%', //'Organizations: %organizations%',

    insights_userFeedback_chart_title: 'User Feedbacks by %unit%s, %dateRange%',
    insights_userFeedback_chart_subtitle: '%groupBy%', //'Organizations: %organizations%',

    insights_visit_chart_tooltip_value_label: '%measure%',
    insights_pageView_chart_tooltip_value_label: 'Page Visited',
    insights_fileDownload_chart_tooltip_value_label: 'Files Downloaded',
    insights_dataQuery_chart_tooltip_value_label: 'Data Queries',
    insights_dataExport_chart_tooltip_value_label: 'Exports',
    insights_pdfExport_chart_tooltip_value_label: 'PDF Exports',
    insights_userFeedback_chart_tooltip_value_label: 'User Feedback',

    insights_columns_date: 'Date',
    insights_columns_value: 'Value',

    analytics_type_visit: 'Visits',
    analytics_type_pageView: 'Pages',
    analytics_type_fileDownload: 'File Downloads',
    analytics_type_dataQuery: 'Data Queries',

    aggregation_function_count: 'Visits',
    aggregation_function_count_unique: 'Unique Visitors',

    aggregation_period_day: 'Day',
    aggregation_period_week: 'Week',
    aggregation_period_month: 'Month',
    aggregation_period_quarter: 'Quarter',
    aggregation_period_year: 'Year',

    analytics_param_day_of_week: 'Day Of Week',
    analytics_param_hour: 'Hour',
    analytics_param_event_id: 'Event Id',
    analytics_param_visit_id: 'Visit Id',
    analytics_param_organization_id: 'Organization',
    analytics_param_account_id: 'Account Id',
    analytics_param_member_id: 'Member Id',
    analytics_param_days_since_registration: 'Days Since Registration',
    analytics_param_is_new_account: 'Is New Account',
    analytics_param_organization_role: 'Organization Role',
    analytics_param_app_path: 'App Path',
    analytics_param_wildcarded_app_path: 'Path',
    analytics_param_source: 'Source',
    analytics_param_app_version: 'App Version',
    analytics_param_app_version_major: 'App Version Major',
    analytics_param_app_version_minor: 'App Version Minor',
    analytics_param_app_version_patch: 'App Version Patch',
    analytics_param_country: 'Country',
    analytics_param_country_code: 'Country Code',
    analytics_param_region: 'Region',
    analytics_param_city: 'City',
    analytics_param_timezone: 'Timezone',
    analytics_param_device_type: 'Device Type',
    analytics_param_device_name: 'Device Name',
    analytics_param_os_name: 'Operating System Name',
    analytics_param_os_version: 'Operating System Version',
    analytics_param_os_version_major: 'Operating System Version Major',
    analytics_param_os_version_minor: 'Operating System Version Minor',
    analytics_param_browser_name: 'Browser Name',
    analytics_param_browser_version: 'Browser Version',
    analytics_param_browser_version_major: 'Browser Version Major',
    analytics_param_browser_version_minor: 'Browser Version Minor',
    analytics_param_browser_language_code: 'Browser Language Code',
    analytics_param_window_width: 'Window Width',
    analytics_param_window_height: 'Window Height',
    analytics_param_prev_app_path: 'Prev App Path',
    analytics_param_wildcarded_prev_app_path: 'Prev Path',
    analytics_param_resource_id: 'Data Collection',
    analytics_param_resource: 'Data Collection Id',
    analytics_param_search_term: 'Search Term',
    analytics_param_resource_name: 'Resource Name',
    analytics_param_fileDownload_resource_name: 'File Name',
    analytics_param_dataQuery_resource_name: 'Data Collection Name',
    analytics_param_resource_category_id: 'Resource Category Id',
    analytics_param_resource_category_name: 'Resource Category Name',
    analytics_param_fileDownload_resource_category_name: 'File Category Name',
    analytics_param_dataQuery_resource_category_name: 'Data Collection Category Name',
    analytics_param_download_time: 'Download Time',
    analytics_param_queried_data_types: 'Data Types',
    analytics_param_queried_granularity: 'Queried Granularity',
    analytics_param_queried_granularity_cardinality_wo_years:
        'Queried Granularity Cardinality Wo Years',
    analytics_param_visible_filters: 'Visible Filters',
    analytics_param_round_id: 'Data Suite',
    analytics_param_round: 'Data Suite Id',
    analytics_param_cause_id: 'Cause',
    analytics_param_cause: 'Cause Id',
    analytics_param_cro: 'Granular Resolution Id',
    analytics_param_comorbid_cause_id: 'Comorbid Cause',
    analytics_param_comorbid_cause: 'Comorbid Cause Id',
    analytics_param_sequela_id: 'Sequela',
    analytics_param_sequela: 'Sequela Id',
    analytics_param_rei_id: 'Rei',
    analytics_param_rei: 'Rei Id',
    analytics_param_covariate_id: 'Covariate',
    analytics_param_covariate: 'Covariate Id',
    analytics_param_sdoh_id: 'SDoH',
    analytics_param_sdoh: 'SDoH Id',
    analytics_param_location_id: 'Location',
    analytics_param_location: 'Location Id',
    analytics_param_age_group_id: 'Age Group',
    analytics_param_age_group: 'Age Group Id',
    analytics_param_gender_id: 'Sex',
    analytics_param_gender: 'Sex Id',
    analytics_param_measure_id: 'Measure',
    analytics_param_measure: 'Measure Id',
    analytics_param_metric_id: 'Metric',
    analytics_param_metric: 'Metric Id',
    analytics_param_forecast_scenario_id: 'Forecast Scenario',
    analytics_param_forecast_scenario: 'Forecast Scenario Id',
    analytics_param_year: 'Year',
    analytics_param_day: 'Day',
    analytics_param_month_count: 'Months (Study)',
    analytics_param_api_time: 'Api Time',
    analytics_param_render_time: 'Render Time',
    analytics_param_chart_types: 'Chart Types',
    analytics_param_format: 'Format',
    analytics_param_reason: 'Reason',
    analytics_param_status: 'Status',
    analytics_param_is_manager_copied: 'Is manager copied',
    analytics_param_pdf_report_options: 'Pdf Report Options',
    analytics_param_delivery_method: 'Delivery Method',
    analytics_param_indicator_id: 'Indicator',
    analytics_param_facility_id: 'Facility',
    analytics_param_race_id: 'Race',
    analytics_param_prompt: 'Prompt',
    analytics_param_response: 'Response',
    analytics_param_reaction: 'Reaction',
    analytics_param_comment: 'Comment',
    analytics_param_industry_id: 'Industry',
    analytics_param_ethnicity_id: 'Ethnicity',
    analytics_param_employment_cohort_id: 'Employment Cohort',
    analytics_param_employment_status_id: 'Employment Status',
    analytics_param_portal_organization_id: 'Portal Organization',
    analytics_param_antibiotic_class_id: 'Antibiotic Class',
    analytics_param_antibiotic_class: 'Antibiotic Class Id',
    analytics_param_counterfactual_id: 'Counterfactual',
    analytics_param_counterfactual: 'Counterfactual Id',
    analytics_param_pathogen_id: 'Pathogen',
    analytics_param_pathogen: 'Pathogen Id',
    analytics_param_risk_exposure: 'Risk Exposure Id',
    analytics_param_multiple_myeloma_treatment_id: 'Multiple Myeloma Treatment',
    analytics_param_education_id: 'Education',
    [`analytics_param_${VEHSS_CONDITION_ID_KEY}`]: 'Eye Condition',
    [`analytics_param_${RISK_EXPOSURE_ID_KEY}`]: 'Risk Exposure',
    [`analytics_param_${PAYER_ID_KEY}`]: 'Payer',
    [`analytics_param_${TOC_ID_KEY}`]: 'Type Of Care',
    [`analytics_param_${SURVEY_ID_KEY}`]: 'Survey',
    [`analytics_param_${QUESTION_ID_KEY}`]: 'Question',
    [`analytics_param_${OPTION_ID_KEY}`]: 'Answer',
    [`analytics_param_${OPTION_COMMENT}`]: 'Comment',
    [`analytics_param_${RESPONDENT_KEY}`]: 'Respondent',
    [`analytics_param_${SURVEY_STATUS}`]: 'Status',
    [`analytics_param_${CREATED}`]: 'Created',
    [`analytics_param_${ORGANIZATION_KEY}`]: 'Organization',
    [`analytics_param_${INFECTIOUS_SYNDROME_ID_KEY}`]: 'Infectious Syndrome',
    analytics_param_infectious_syndrome: 'Infectious Syndrome Id',
    [`analytics_param_${VACCINATION_STATUS_ID_KEY}`]: 'Vaccination Status',
    [`analytics_param_${INFECTION_TYPE_ID_KEY}`]: 'Infection Type',
    [`analytics_param_${SEVERITY_ID_KEY}`]: 'Severity',
    [`analytics_param_${SYMPTOMATIC_ID_KEY}`]: 'Symptomatic',
    [`analytics_param_${APPARENT_ID_KEY}`]: 'Apparent',
    [`analytics_param_${OUTCOME_ID_KEY}`]: 'Dengue Case Outcome',
    [`analytics_param_${CRO_ID_KEY}`]: 'Granular Resolution',

    source_web: 'Web',
    source_api: 'API',
    'source_ai-chat': 'IHME Chat',
    'source_ai-mcp': 'AI MCP',

    chart_type_line: 'Line',
    chart_type_table: 'Table',
    chart_type_bar: 'Bar',
    chart_type_map: 'Map',
    chart_type_treemap: 'Treemap',
    chart_type_arrow: 'Arrow',
    chart_type_scatter_map: 'Scatter Map',
    chart_axis_label_year: ' ',

    delivery_method_download: 'Downloads',
    delivery_method_email: 'Emailed',

    pdf_report_option_filter: 'Filter',
    pdf_report_option_cdn: 'CDN',
    pdf_report_option_chart: 'Chart',
    pdf_report_option_data_table: 'Data Table',

    format_csv: 'CSV',
    format_png: 'PNG',
    format_xls: 'XLS',
    format_pdf: 'PDF',
    format_gif: 'GIF',

    status_open: 'Open',

    reason_suggestion: 'Suggestion',
    reason_question: 'Question',
    reason_bug: 'Bug',
    reason_other: 'Other',

    //Disable Organization Access Modal
    disable_organization_modal_title: 'Disable access to Client Portal for this organization',
    disable_organization_modal_message: 'Are you sure?',

    okay_label: 'Okay',

    // Content Management

    // // Welcome Message
    welcome_message_title: 'Welcome Message',
    welcome_message_add_button: 'Add Message',
    welcome_message_add_custom_button: 'Add custom message',
    welcome_message_edit_custom_button: 'Edit custom message',
    welcome_message_edit_button: 'Edit Message',
    welcome_message_field_name: 'Name',
    welcome_message_field_title: 'Header',
    welcome_message_field_text: 'Body',
    welcome_message_empty_state_header: 'No custom welcome message has been added',
    welcome_message_empty_state_body:
        'You can add a custom welcome message for this organization. This will override the default message.',

    // // News And Announcements Tab
    news_and_announcements_title: 'News & Announcements',
    news_and_announcements_share_title: 'Share Post',
    news_and_announcements_delete_title: 'Delete Post',
    news_and_announcements_add_button: 'Add Post',
    news_and_announcements_add_title: 'Add Post',
    news_and_announcements_edit_title: 'Edit Post',
    news_and_announcements_column_name: 'Post',
    news_and_announcements_column_shared_with: 'Shared',
    news_and_announcements_column_created_by: 'Created By',
    news_and_announcements_column_actions: 'Actions',
    news_and_announcements_status_filter_label: 'Status',
    news_and_announcements_status_filter_is_live_label: 'Live',
    news_and_announcements_status_filter_is_not_live_label: 'Not Live',
    news_and_announcements_status_filter_all_label: 'All Posts',
    news_and_announcements_organization_filter_label: 'Organization',
    // // News And Announcements Add/Edit
    news_and_announcements_field_title: 'Title',
    news_and_announcements_field_url: 'Url',
    news_and_announcements_publish_section_label: 'Publish this post:',
    news_and_announcements_publish_date_label: 'Publish Date',
    news_and_announcements_publish_time_label: 'Publish Time',
    news_and_announcements_publish_option_1: 'Immediately',
    news_and_announcements_publish_option_2: 'Select date and time',
    news_and_announcements_share_with_section_label: 'Share this post with:',
    news_and_announcements_share_with_label: 'Organizations',
    news_and_announcements_share_with_option_1: 'All organizations',
    news_and_announcements_share_with_option_2: 'Select organizations',

    // // Sidebar Links Tab
    sidebar_links_title: 'Sidebar Links (IHME Tools)',
    sidebar_links_share_title: 'Share Link',
    sidebar_links_delete_title: 'Delete Link',
    sidebar_links_add_button: 'Add Link',
    sidebar_links_add_title: 'Add Sidebar Link',
    sidebar_links_edit_title: 'Edit Sidebar Link',
    sidebar_links_column_name: 'Link',
    sidebar_links_column_tooltip: 'Tooltip',
    sidebar_links_column_shared_with: 'Shared',
    sidebar_links_column_created_by: 'Created By',
    sidebar_links_column_actions: 'Actions',
    sidebar_links_organization_filter_label: 'Organization',
    sidebar_links_all_organizations: 'All',

    // // Sidebar Links Add/Edit
    sidebar_links_field_title: 'Link Name',
    sidebar_links_field_icon: 'Icon',
    sidebar_links_field_url: 'Url',
    sidebar_links_field_hint: 'Tooltip',
    sidebar_links_share_with_section_label: 'Share this link with:',
    sidebar_links_share_with_label: 'Organizations',
    sidebar_links_share_with_option_1: 'All organizations',
    sidebar_links_share_with_option_2: 'Select organizations',

    // // Welcome Messages Tab
    welcome_messages_add_button: 'Add Message',
    welcome_messages_name: 'Name',
    welcome_messages_is_default: 'Is Default',
    welcome_messages_date: 'Date',
    welcome_messages_created_by: 'Created By',
    welcome_messages_actions: 'Actions',
    welcome_message_delete_title: 'Delete welcome message',

    // Methodology Tab
    methodology_title: 'Methodology',
    methodology_add_title: 'Add Methodology',
    methodology_edit_title: 'Edit Methodology',
    methodology_delete_title: 'Delete Methodology',
    methodology_column_title: 'Name',
    methodology_column_description: 'Description',
    methodology_column_creator: 'Created by',
    methodology_column_actions: 'Actions',
    methodology_field_title: 'Name',
    methodology_field_description: 'Description',
    methodology_field_url: 'Url',
    methodology_field_file_label: 'File',
    methodology_field_image_url: 'Image',
    methodology_content_type_label: 'Choose content type:',
    methodology_content_type_option_1: 'Link',
    methodology_content_type_option_2: 'File',
    methodology_message_field_url: 'Link or File',

    // // License Expiration
    license_expiration_date: 'Expiration date',
    license_expiration_time: 'Time',
    license_expiration_save_button: 'Save Changes',
    license_expiration_expiration_message: 'Expiration Message',
    license_expiration_is_active: 'License expiration is active',
    license_expiration_is_disabled: 'License expiration is disabled',
    license_expiration_message_success: 'License expiration changes were saved',
    license_expiration_message_expired: 'Organization license is already expired.',
    license_expiration_message_info:
        'Expiration message will appear in Dashboard 30 days prior to expiration date.',

    // Settings Scene
    // // Smart Options
    smart_options_add_title: 'Add Smart Option',
    smart_options_create_hint:
        'After saving, your Smart Option will become immediately available in Staff Portal Data Collection editor for all Staff Portal users.',
    smart_options_edit_title: 'Edit Smart Option',
    smart_options: 'Smart Options',
    smart_options_add_button: 'Add Smart Option',
    smart_options_field_name: 'Name',
    smart_options_field_name_placeholder: 'Name',
    smart_options_field_description: 'Description',
    smart_options_field_description_placeholder: 'Description',
    smart_options_field_filter: 'Filter',
    smart_options_field_filter_placeholder: 'Select Filter...',
    smart_options_header_name: 'Name',
    smart_options_header_description: 'Description',
    smart_options_header_filter_affected: 'Filter Affected',
    smart_options_header_created: 'Created',
    smart_options_header_actions: 'Actions',
    smart_options_header_created_by: 'Created By',
    smart_option_delete_title: 'Delete Smart Option',
    smart_option_save_selection_label: 'Save current selection as a new smart option',
    create_smart_option_modal_title: 'How would you like to name this selection?',

    data_type_cause_associated_coprevalence_records: 'Associated Current Co-Prevalence',

    // condition name overwrites because of identical names for some ids
    cause_1021: 'Liver cancer due to other causes (GBD 2019)',
    cause_1022: 'Other malignant neoplasms (GBD 2019)',
    cause_1023: 'Other cardiovascular and circulatory diseases (GBD 2019)',

    member_status_invited: 'Invited',
    member_status_activated: 'Activated',
    member_status_unconfirmed: 'Unconfirmed Email',

    insights_export_pdf: 'Export PDF',

    organizations_dropdown_placeholder: 'Select organizations...',
    organization_dropdown_placeholder: 'Select organization...',

    back_to_edit_button: 'Back to Edit',
    publish_button: 'Publish',
    preview_survey_button: 'Preview Survey',
    add_question_button: 'Add Question',
    add_option_button: 'Add Option',
    add_other_option_button: 'Add "Other"',

    survey_question_number: 'Question %number%',
    survey_option_number: 'Option %number%',

    survey_question_type_single: 'Single choice',
    survey_question_type_multi: 'Multiple choice',
    survey_question_type_text: 'Short answer',

    survey_question_type_label: 'Type',

    survey_editor_caption: 'General Information',
    survey_editor_name_label: 'Internal name',
    survey_editor_title_label: 'Title',
    survey_editor_description_label: 'Description',
    survey_editor_type_label: 'Type',
    survey_editor_question_label: 'Question',

    org_already_shared_with_survey: '* already has a survey',
    survey_active_organization_hint_title:
        'This action will overwrite the existing survey for the following organization(s):',
    survey_active_organization_hint_confirmation: 'Do you want to continue?',
};
